import React, { useContext, useEffect, useState } from "react";
import { ListProps } from "./Index";
import { Operation, TableHeaders } from "../../../../../utils/Enum.types";
import { Title } from "../../../../../stories/Title/Title";
import { TableHeaderProps, msgType } from "../../../../../utils/types";
import {
  HEADER_TEXT_ALIGN,
  SLNO_TEXT_ALIGN,
  TABLE_ROW_HEIGHT,
} from "../../../../../styles/Variables";
import { GridColDef, GridValidRowModel } from "@mui/x-data-grid-pro";
import Edit from "../../../../../images/EditProfile.svg";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import DeleteModal from "../../../../../customComponents/DeleteModal";
import { useLazyQuery, useMutation } from "@apollo/client";
import { getModifiedScrollHeight } from "../../../../../utils/UtilFunctions";
import {
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
  emptyMessage,
} from "../../../../../utils/constants";

import useUrlParams from "../../../../../customHooks/useUrlParams";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { AppContext } from "../../../../../context/context";
import { payloadTypes } from "../../../../../context/reducer";
import { GetStockGroups } from "./queries/queries";
import {
  AtsStckGroupEdge,
  AtsStckGroupOrderField,
  DeleteGroupByIdResult,
  DeleteGroupByIdVars,
  GetStockGroupsResult,
  GetStockGroupsVars,
} from "./queries/types";
import { DeleteStockGroup } from "./queries/mutations";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import LoadingModal from "../../../../pages/LoadingModal";
import Delete from "../../../../../images/Delete.svg";
import MessageModal from "../../../../../customComponents/MessageModal";
import useLabels from "../../../../../customHooks/useLabels";

const List = ({ setOperation }: ListProps) => {
  const { customer_id } = useUrlParams();
  const [searchData, setSearchData] = useState("");
  const { state, dispatch } = useContext(AppContext);
  const [GetGroups, { data, loading, fetchMore }] = useLazyQuery<
    GetStockGroupsResult,
    GetStockGroupsVars
  >(GetStockGroups);
  const { user_details } = useLoggedInUserDetails();

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [categories, setCategories] = useState<AtsStckGroupEdge[]>([]);
  const [rows, setRows] = useState<GridValidRowModel[]>([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [message, setMessage] = useState(emptyMessage);
  const { itemLabel } = useLabels();
  const [DeleteGroup, { loading: deletionLoading }] = useMutation<
    DeleteGroupByIdResult,
    DeleteGroupByIdVars
  >(DeleteStockGroup, {
    onError: (e) =>
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      }),
  });

  const dynamicHeaders: TableHeaderProps[] = [
    {
      headerName: "Sl",
      field: "id",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
    },
    {
      headerName: `Item Group Description`,
      field: "group_name",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      flex: 1,
      hideable: false,
    },
  ];
  const columns: GridColDef[] = [
    ...dynamicHeaders,
    {
      headerName: TableHeaders.ACTION,
      field: "actions",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      renderCell: (params) => {
        return (
          <>
            <img
              src={Edit}
              alt=""
              onClick={() => {
                dispatch({
                  type: payloadTypes.SET_ITM_GROUP_ID,
                  payload: {
                    itmGroupId: params.row.group_id,
                  },
                });
                setOperation(Operation.UPDATE);
              }}
            />
            <img
              src={Delete}
              alt=""
              onClick={() => {
                dispatch({
                  type: payloadTypes.SET_ITM_GROUP_ID,
                  payload: {
                    itmGroupId: params.row.group_id,
                  },
                });
                setDeleteModal(!deleteModal);
              }}
            />
          </>
        );
      },
    },
  ];

  const HandleDelete = async () => {
    setDeleteModal(!deleteModal);
    DeleteGroup({
      variables: {
        group_id: state.itmGroupId,
        user_details,
      },
      refetchQueries: [
        {
          query: GetStockGroups,
          variables: {
            after: null,
            customerID: customer_id,
            first: ROWS_PER_PAGE,
            orderBy: {
              direction: OrderDirection.ASC,
              field: AtsStckGroupOrderField.GROUP_NAME,
            },

            name: searchData,
          },
        },
      ],
    }).then(({ data }) => {
      if (data) {
        setMessage({
          flag: true,
          message: `${itemLabel} Group Deleted Sucessfully`,
          operation: Operation.DELETE,
        });
        dispatch({
          type: payloadTypes.SET_ITM_GROUP_ID,
          payload: {
            itmGroupId: 0,
          },
        });
      }
    });
  };

  const handleClose = () => {
    setMessage({
      message: "",
      flag: false,
      operation: Operation.NONE,
    });
  };

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetStockGroups.edges;
              const pageInfo = fetchMoreResult.GetStockGroups.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetStockGroups.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetStockGroups: {
                  edges: [...categories, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetStockGroups.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable && rows.length)
      scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [rows]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetStockGroups.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = categories.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setCategories(updatedNewData);
        setRows(
          updatedNewData.map(({ node }, index) => ({
            id: index + 1,
            group_name: node.groupName,
            group_id: node.id,
          }))
        );
      } else {
        setCategories(newData);
        setRows(
          newData.map(({ node }, index) => ({
            id: index + 1,
            group_name: node.groupName,
            group_id: node.id,
          }))
        );
      }
      setEndCursor(data.GetStockGroups.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    if (customer_id) {
      GetGroups({
        variables: {
          after: null,
          customerID: customer_id,
          first: ROWS_PER_PAGE,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsStckGroupOrderField.GROUP_NAME,
          },

          name: searchData,
        },
      });
    }
  }, [customer_id, GetGroups, searchData]);

  return (
    <>
      <div className="master-data__list">
        <Title variant="subtitle1">{`List of item group`}</Title>
        <div className="master-data__list--tableblock">
          <StyledDatagrid
            columns={columns}
            rows={rows}
            rowHeight={TABLE_ROW_HEIGHT}
            // onCellClick={(params) => {
            //   if (params.field === "months") {
            //     setViewModal(!viewModal);
            //     handleFilterMonthDetails(params.row.month_id);
            //   }
            // }}
            disableRowSelectionOnClick
            hideFooter
          />
        </div>
      </div>

      <LoadingModal flag={deletionLoading || loading} />

      <DeleteModal
        id={state.itmGroupId}
        modalFlag={deleteModal}
        setModalFlag={setDeleteModal}
        handleDelete={HandleDelete}
      />
      <MessageModal
        modalFlag={message.flag!}
        value={message.message!}
        handleClose={handleClose}
        operation={message.operation!}
      />
    </>
  );
};

export default List;
